import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { Send, MessageSquare, Search } from "lucide-react";

export const MessagesView = () => {
  const { user } = useAuth();
  const { convId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const endRef = useRef(null);

  const loadConversations = async () => {
    try {
      const r = await api.get("/conversations/");
      setConversations(r.data || []);
    } catch (e) {}
  };

  useEffect(() => { loadConversations(); }, []);
  useEffect(() => {
    if (convId) {
      api.get(`/conversations/${convId}/messages`).then(r => setMessages(r.data || [])).catch(() => setMessages([]));
    } else if (conversations.length > 0 && !convId) {
      const base = user?.role === "admin" ? "/admin" : user?.role === "manager" ? "/manager" : "/app";
      navigate(`${base}/messages/${conversations[0].id}`);
    }
  }, [convId, conversations.length]);

  useEffect(() => {
    const id = setInterval(() => {
      if (convId) api.get(`/conversations/${convId}/messages`).then(r => setMessages(r.data || [])).catch(() => {});
      loadConversations();
    }, 15000);
    return () => clearInterval(id);
  }, [convId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !convId) return;
    try {
      await api.post(`/conversations/${convId}/messages`, { content: text });
      setText("");
      const r = await api.get(`/conversations/${convId}/messages`);
      setMessages(r.data || []);
      loadConversations();
    } catch (e) {}
  };

  const baseUrl = user?.role === "admin" ? "/admin" : user?.role === "manager" ? "/manager" : "/app";
  const filtered = conversations.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.last_message_preview || "").toLowerCase().includes(s) || (c.participants_info || []).some(p => (p.full_name || "").toLowerCase().includes(s));
  });

  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Messaggi</h1>
      <p className="text-sm text-slate-600 mb-4">Conversazioni con il team CXOORA e con i partecipanti dei progetti.</p>
      <div className="cxo-card overflow-hidden flex h-[70vh]">
        <aside className="w-72 border-r border-slate-200 flex flex-col">
          <div className="p-3 border-b border-slate-200 relative">
            <Search size={14} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-7" placeholder="Cerca" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.map(c => {
              const others = (c.participants_info || []).filter(p => p.id !== user?.id);
              return (
                <button key={c.id} onClick={() => navigate(`${baseUrl}/messages/${c.id}`)} className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 ${convId === c.id ? "bg-[#EAF0FF]" : ""}`} data-testid={`conv-${c.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-slate-900 truncate">{others.map(o => o.full_name).join(", ") || "Conversazione"}</p>
                    {c.unread > 0 && <Badge className="bg-[#2D6BFF] text-white border-0 h-5 px-1.5 text-[10px]">{c.unread}</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{c.last_message_preview || ""}</p>
                </button>
              );
            })}
            {!filtered.length && <p className="text-sm text-slate-500 p-4 text-center">Nessuna conversazione</p>}
          </div>
        </aside>
        <section className="flex-1 flex flex-col bg-slate-50">
          {convId && messages.length > 0 ? (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {messages.map(m => (
                  <div key={m.id} className={`flex ${m.sender_id === user?.id ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] p-3 rounded-2xl ${m.sender_id === user?.id ? "bg-[#2D6BFF] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-900 rounded-bl-sm"}`}>
                      <p className="text-sm">{m.content}</p>
                      <p className={`text-[10px] mt-1 ${m.sender_id === user?.id ? "text-white/70" : "text-slate-500"}`}>{new Date(m.created_at).toLocaleString("it-IT")}</p>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="border-t border-slate-200 p-3 bg-white flex gap-2">
                <Input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder="Scrivi un messaggio..." data-testid="msg-input" />
                <Button onClick={send} className="bg-[#2D6BFF] text-white" data-testid="msg-send"><Send size={16} /></Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
              <div className="text-center">
                <MessageSquare size={40} className="mx-auto text-slate-300" />
                <p className="mt-3">Seleziona una conversazione</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
